import { useEffect, useState } from "react";
import { ShieldCheck, Activity, Languages, Map, FileText, LogOut } from "lucide-react";
import { useT, useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import brandLogo from "../assets/resilio-logo.webp";

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const utc = now.toISOString().slice(11, 19);
  return (
    <div className="flex items-center gap-2 font-mono text-xs text-slate-400" data-testid="utc-clock">
      <span className="text-slate-500">UTC</span>
      <span className="text-cyan-300 tabular-nums">{utc}</span>
    </div>
  );
}

export const CommandBar = ({ summary, setLang, view, setView, danaMode, setDanaMode, radarStatus }) => {
  const t = useT();
  const lang = useLang();
  const { logout } = useAuth();

  return (
    <header
      className="h-14 shrink-0 flex items-center justify-between px-4 bg-[#0B0F19] border-b border-slate-800"
      data-testid="command-bar"
    >
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2.5" data-testid="command-bar-title">
          <img src={brandLogo} alt="RESILIO.ES · Inteligencia antes del impacto" className="h-10 w-auto object-contain" data-testid="brand-logo" />
        </div>

        <div className="hidden md:flex items-center gap-2 pl-4 border-l border-slate-800">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-[10px] font-mono text-emerald-400 tracking-wider"
            data-testid="system-status-badge"
          >
            <Activity className="h-3 w-3" />
            OPERATIONAL
          </span>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-slate-900 border border-slate-800 text-[10px] font-mono text-cyan-400 tracking-wider"
            data-testid="epsg-projection-badge"
          >
            <ShieldCheck className="h-3 w-3" />
            EPSG:25830 VERIFIED
          </span>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-[10px] font-mono tracking-wider ${
              radarStatus?.live
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                : "bg-red-500/10 border-red-500/30 text-red-400"
            }`}
            data-testid="radar-health-dot"
            title={radarStatus?.live ? t.radar_health_live : t.radar_health_down}
          >
            <span className={`h-2 w-2 rounded-full ${radarStatus?.live ? "bg-emerald-400 animate-pulse" : "bg-red-500"}`} />
            {t.radar_label}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1 bg-[#111726] rounded-md p-1 border border-slate-800" data-testid="view-nav">
          <button
            onClick={() => setView("map")}
            data-testid="nav-map"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider rounded transition-colors ${
              view === "map"
                ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/50 shadow-[0_0_12px_rgba(8,145,178,.25)]"
                : "text-slate-300 bg-[#0B0F19] border border-slate-700 hover:text-cyan-300 hover:border-cyan-500/40"
            }`}
          >
            <Map className="h-3.5 w-3.5" /> {t.nav_map}
          </button>
          <button
            onClick={() => setView("reports")}
            data-testid="nav-reports"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider rounded transition-colors ${
              view === "reports"
                ? "bg-cyan-500/20 text-cyan-200 border border-cyan-500/50 shadow-[0_0_12px_rgba(8,145,178,.25)]"
                : "text-slate-300 bg-[#0B0F19] border border-slate-700 hover:text-cyan-300 hover:border-cyan-500/40"
            }`}
          >
            <FileText className="h-3.5 w-3.5" /> {t.nav_reports}
          </button>
        </div>
        <button
          onClick={() => setDanaMode(!danaMode)}
          data-testid="dana-toggle"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono tracking-wider rounded border transition-colors ${
            danaMode
              ? "bg-amber-500/20 border-amber-500/60 text-amber-300"
              : "bg-[#111726] border-slate-700 text-slate-400 hover:text-amber-300 hover:border-amber-500/40"
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${danaMode ? "bg-amber-400 animate-pulse" : "bg-slate-500"}`} />
          {t.dana_toggle}
        </button>
        <div className="hidden lg:flex items-center gap-3 font-mono text-[11px]" data-testid="threat-ticker">
          <span className="text-slate-500">{t.anomalies}</span>
          <span className="text-red-400">● {summary?.critical ?? 0} {t.tick_crit}</span>
          <span className="text-amber-400">● {summary?.warning ?? 0} {t.tick_warn}</span>
          <span className="text-emerald-400">● {summary?.info ?? 0} {t.tick_ops}</span>
        </div>
        <button
          onClick={() => setLang(lang === "es" ? "en" : "es")}
          data-testid="language-toggle"
          title={lang === "es" ? "Switch to English" : "Cambiar a Español"}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-[#111726] border border-slate-700 text-[11px] font-mono text-slate-200 hover:border-cyan-500 hover:text-cyan-300 transition-colors"
        >
          <Languages className="h-3.5 w-3.5" />
          <span className={lang === "es" ? "text-cyan-300" : "text-slate-500"}>ES</span>
          <span className="text-slate-700">/</span>
          <span className={lang === "en" ? "text-cyan-300" : "text-slate-500"}>EN</span>
        </button>
        <Clock />
        <button
          onClick={logout}
          data-testid="logout-button"
          title={t.logout}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-[#111726] border border-slate-700 text-[11px] font-mono text-slate-300 hover:border-red-500/50 hover:text-red-300 transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{t.logout}</span>
        </button>
      </div>
    </header>
  );
};
